abstract class BaseDomainEntity<TEntity extends BaseDomainEntity<TEntity, TData>, TData extends { id: string }> {
  protected readonly id: string;

  constructor(data: TData) {
    this.id = data.id;
  }

  getId(): string {
    return this.id;
  }

  static register<T>(
    this: { idRegistry: Map<string, T> },
    params: { id: string, instance: T },
  ): void {
    this.idRegistry.set(params.id, params.instance);
  }

  static initialize<T, TData extends { id: string }>(
    this: { idRegistry: Map<string, T> },
    dataList: TData[],
    createEntity: (data: TData) => T | null,
  ): void {
    this.idRegistry.clear();

    if (!dataList?.length) {
      return;
    }

    dataList.forEach((data) => {
      const entity = createEntity(data);
      if (!entity) {
        return;
      }

      this.idRegistry.set(data.id, entity);
    });
  }

  static getAll<T>(
    this: { idRegistry: Map<string, T> },
  ): T[] {
    return Array.from(this.idRegistry.values());
  }

  static getCollection<TData = unknown>(): TData[] {
    const registry = (this as unknown as { idRegistry: Map<string, unknown> }).idRegistry;
    return Array.from(registry.values()).map(
      (entity) => Object.fromEntries(Object.entries(entity)) as TData,
    );
  }

  static from<T>(
    this: {
      idRegistry: Map<string, T>,
      getId?: (abbreviation: string) => string | null,
    },
    identifier: string,
  ): T | null {
    const byId = this.idRegistry.get(identifier);
    if (byId) {
      return byId;
    }

    if (!this.getId) {
      return null;
    }

    const id = this.getId(identifier);
    if (!id) {
      return null;
    }

    return this.idRegistry.get(id) || null;
  }

  static getIds<T extends { getId(): string }>(
    this: {
      idRegistry: Map<string, T>,
      getId?: (abbreviation: string) => string | null,
      from: (identifier: string) => T | null,
    },
    identifiers: string | string[],
  ): string[] {
    if (!identifiers) {
      return [];
    }

    const identifiersArray = Array.isArray(identifiers)
      ? identifiers
      : identifiers.split(',').map((id) => id.trim());

    if (!identifiersArray.length) {
      return [];
    }

    return identifiersArray
      .map((identifier) => {
        const entity = this.from(identifier);
        return entity ? entity.getId() : null;
      })
      .filter((id): id is string => id !== null);
  }

  static getAbbreviations(
    this: { getAbbreviation: (identifier: string) => string | null },
    identifiers: string | string[],
  ): string[] {
    if (!identifiers) {
      return [];
    }

    const identifiersArray = Array.isArray(identifiers)
      ? identifiers
      : identifiers.split(',').map((id) => id.trim());

    if (!identifiersArray.length) {
      return [];
    }

    return identifiersArray
      .map((identifier) => this.getAbbreviation(identifier))
      .filter((abbreviation): abbreviation is string => abbreviation !== null);
  }

  static getDisplayNames<T extends { getId(): string, getDisplayName(): string }>(
    this: {
      idRegistry: Map<string, T>,
      getId?: (abbreviation: string) => string | null,
      from: (identifier: string) => T | null,
      getIds: (identifiers: string | string[]) => string[],
    },
    identifiers: string | string[],
  ): string[] {
    const entityIds = this.getIds(identifiers);
    return entityIds
      .map((id) => this.from(id)?.getDisplayName())
      .filter((name): name is string => name !== undefined);
  }
}

export default BaseDomainEntity;
